/**
 * Participation inbox — the receiving side (PRD Part D).
 *
 * Until this existed, the request lifecycle was fully modelled and entirely
 * self-reported: the requesting planner flipped the status themselves. One rule
 * changes that — a sent request is answered by its counterpart, enforced by
 * migration 690, not by this component's good manners.
 *
 * A decline is not a dead end: it surfaces on the affected priority as the
 * At Risk - Participation overlay the engine already computes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Clock, Inbox, Loader2, MinusCircle, XCircle } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import {
  getParticipationInbox,
  isAnswered,
  isAwaitingResponse,
  isOverdue,
  respondToParticipationRequest,
  type ParticipationResponse,
} from '../../../lib/planningParticipationService';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { TeamParticipation } from '../../../lib/planningEngineModule.types';

const RESPONSES: Array<{
  value: ParticipationResponse;
  label: string;
  icon: typeof Check;
  className: string;
}> = [
  {
    value: 'confirmed',
    label: 'Accept',
    icon: Check,
    className: 'bg-[var(--green)] text-white hover:bg-[var(--green)]',
  },
  {
    value: 'tentative',
    label: 'Tentative',
    icon: MinusCircle,
    className:
      'border border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] dark:border-[var(--mustard)] dark:text-[var(--mustard)]',
  },
  {
    value: 'declined',
    label: 'Decline',
    icon: XCircle,
    className:
      'border border-[var(--coral)] text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:border-[var(--coral)] dark:text-[var(--coral)]',
  },
];

function StatusChip({ row }: { row: TeamParticipation }) {
  if (isAwaitingResponse(row)) {
    const overdue = isOverdue(row);
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          overdue
            ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]'
            : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--rule)] dark:text-[var(--text-sec)]'
        }`}
      >
        {overdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
        {overdue ? 'Overdue' : 'Awaiting your answer'}
      </span>
    );
  }
  const cls =
    row.requestStatus === 'confirmed'
      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
      : row.requestStatus === 'declined'
        ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]'
        : 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {row.requestStatus}
    </span>
  );
}

function RequestRow({
  row,
  onResponded,
}: {
  row: TeamParticipation;
  onResponded: (response: ParticipationResponse) => void;
}) {
  const [busy, setBusy] = useState<ParticipationResponse | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const respond = async (response: ParticipationResponse) => {
    setBusy(response);
    setError(null);
    try {
      await respondToParticipationRequest({
        participationId: row.id,
        response,
        note: note.trim() || null,
      });
      onResponded(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const periods = row.periodCommitments.length;

  return (
    <div className="px-5 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {row.requiredTeam}
            {row.requiredCapability ? ` · ${row.requiredCapability}` : ''}
          </p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
            {row.capacityRequiredWu != null && `${row.capacityRequiredWu} WU asked`}
            {periods > 0 && `${row.capacityRequiredWu != null ? ' · ' : ''}${periods} period${periods === 1 ? '' : 's'}`}
            {row.participationDeadline && ` · needed by ${row.participationDeadline}`}
          </p>
        </div>
        <StatusChip row={row} />
      </div>

      {row.riskIfUnavailable && (
        <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mb-2">
          If unavailable: {row.riskIfUnavailable}
        </p>
      )}

      {error && <p className="text-xs text-[var(--coral)] mb-2">{error}</p>}

      {isAwaitingResponse(row) ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)"
            className="flex-1 min-w-[160px] px-2.5 py-1.5 text-xs border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
          />
          {RESPONSES.map(({ value, label, icon: Icon, className }) => (
            <button
              key={value}
              type="button"
              onClick={() => void respond(value)}
              disabled={busy != null}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5  text-xs font-medium disabled:opacity-50 ${className}`}
            >
              {busy === value ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
              {label}
            </button>
          ))}
        </div>
      ) : (
        isAnswered(row) && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Answered {row.respondedAt ? new Date(row.respondedAt).toLocaleString() : ''}
            {row.responseNote ? ` — “${row.responseNote}”` : ''}
          </p>
        )
      )}
    </div>
  );
}

export default function ParticipationInbox() {
  const { projectId } = usePlanningEngine();
  const userId = useUserStore((s) => s.userId);
  const [rows, setRows] = useState<TeamParticipation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Next step after answering: what the answer does, and where it shows.
  const [answered, setAnswered] = useState<ParticipationResponse | null>(null);

  const load = useCallback(async () => {
    if (!projectId || !userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setRows(await getParticipationInbox(projectId, userId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCount = useMemo(() => rows.filter(isAwaitingResponse).length, [rows]);

  // Nothing addressed to this person — say nothing rather than showing an
  // empty card on every planner's dashboard.
  if (!loading && rows.length === 0 && !error) return null;

  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <Inbox className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
        <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Asked of you
        </span>
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] ml-auto">
          {openCount > 0 ? `${openCount} awaiting your answer` : 'All answered'}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <p className="px-5 py-4 text-sm text-[var(--coral)]">{error}</p>
      ) : (
        <>
          {answered && (
            <p role="status" className="px-5 py-2.5 text-xs text-[var(--text-sec)] border-b border-[var(--rule)]">
              {answered === 'declined'
                ? 'You declined. The requester has been told, and the priority is flagged At Risk - Participation on their plan.'
                : answered === 'tentative'
                  ? 'You answered tentatively. The requester has been told; answer again once you know.'
                  : 'You accepted. The requester has been told, and this commitment now counts on their plan.'}{' '}
              <Link to="/planning/engine/above-below" className="text-primary-600 hover:underline">
                See it on the plan
              </Link>
            </p>
          )}
          {rows.map((row) => (
            <RequestRow
              key={row.id}
              row={row}
              onResponded={(response) => {
                setAnswered(response);
                void load();
              }}
            />
          ))}
        </>
      )}

      <p className="px-5 py-2.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
        These are cross-functional asks from other people's plans. A decline is not a dead end — it
        surfaces on the affected priority as{' '}
        <Link to="/planning/engine/above-below" className="text-primary-600 hover:underline">
          At Risk - Participation
        </Link>
        .
      </p>
    </div>
  );
}
